import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, Users, Mail, Package, Trash2, Eye, UserPlus, Edit, FileText, Plus, Upload, Copy, Image, X, Save, AlertTriangle, Calendar, User, Tag, ExternalLink, ShoppingBag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import ProductManagement from '../components/admin/ProductManagement';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [enquiries, setEnquiries] = useState([]);
  const [exports, setExports] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('enquiries');
  const [stats, setStats] = useState({
    total_enquiries: 0,
    new_today: 0,
    total_exports: 0
  });
  
  // New user form
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'admin'
  });
  
  // Edit user form
  const [editUser, setEditUser] = useState(null);
  const [editUserForm, setEditUserForm] = useState({
    email: '',
    full_name: '',
    password: '',
    role: ''
  });
  
  // Blog post form
  const [blogForm, setBlogForm] = useState({
    blog_id: '',
    title: '',
    subtitle: '',
    content: '',
    image_url: '',
    hashtags: '',
    published: true
  });
  
  // Blog preview
  const [previewBlog, setPreviewBlog] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  
  // Status update
  const [statusUpdate, setStatusUpdate] = useState({
    enquiry_id: '',
    status: '',
    remarks: ''
  });
  
  // File upload
  const [uploadingFile, setUploadingFile] = useState(false);
  
  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState({
    open: false,
    type: '', // 'user', 'blog', 'file', 'enquiry'
    id: '',
    name: ''
  });
  const [deleting, setDeleting] = useState(false);
  
  const [isAddUserOpen, setIsAddUserOpen] = useState(false);
  const [isEditUserOpen, setIsEditUserOpen] = useState(false);
  const [isStatusUpdateOpen, setIsStatusUpdateOpen] = useState(false);
  const [isBlogFormOpen, setIsBlogFormOpen] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin-login');
      return;
    }
    fetchData();
  }, [navigate]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const [enquiriesRes, exportsRes, usersRes, blogsRes, filesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/enquiries`, config),
        axios.get(`${BACKEND_URL}/api/admin/exports`, config),
        axios.get(`${BACKEND_URL}/api/admin/users`, config),
        axios.get(`${BACKEND_URL}/api/admin/blog/posts`, config),
        axios.get(`${BACKEND_URL}/api/admin/files`, config).catch(() => ({ data: { files: [] } }))
      ]);

      setEnquiries(enquiriesRes.data.enquiries || []);
      setExports(exportsRes.data.exports || []);
      setAdminUsers(usersRes.data.users || []);
      setBlogPosts(blogsRes.data.posts || []);
      setFiles(filesRes.data.files || []);
      
      // Calculate stats
      const today = new Date().toDateString();
      const newToday = (enquiriesRes.data.enquiries || []).filter(e => 
        new Date(e.created_at).toDateString() === today
      ).length;

      setStats({
        total_enquiries: (enquiriesRes.data.enquiries || []).length,
        new_today: newToday,
        total_exports: (exportsRes.data.exports || []).length
      });
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin-login');
  };

  const handleDelete = async (enquiryId) => {
    setDeleting(true);
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(
        `${BACKEND_URL}/api/admin/enquiries/${enquiryId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Enquiry deleted successfully');
      setDeleteConfirm({ open: false, type: '', id: '', name: '' });
      fetchData();
    } catch (error) {
      console.error('Error deleting enquiry:', error);
      toast.error('Failed to delete enquiry');
    } finally {
      setDeleting(false);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(
        `${BACKEND_URL}/api/admin/users`,
        newUser,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('User added successfully');
      setIsAddUserOpen(false);
      setNewUser({ email: '', password: '', full_name: '', role: 'admin' });
      fetchData();
    } catch (error) {
      console.error('Error adding user:', error);
      toast.error(error.response?.data?.detail || 'Failed to add user');
    }
  };

  const handleDeleteUser = async (userId) => {
    setDeleting(true);
    try {
      const token = localStorage.getItem('admin_token');
      console.log('Deleting user:', userId);
      const response = await axios.delete(
        `${BACKEND_URL}/api/admin/users/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('Delete response:', response.data);
      toast.success('User deleted successfully');
      setDeleteConfirm({ open: false, type: '', id: '', name: '' });
      fetchData();
    } catch (error) {
      console.error('Error deleting user:', error);
      console.error('Error response:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  const openDeleteConfirm = (type, id, name) => {
    setDeleteConfirm({ open: true, type, id, name });
  };

  const executeDelete = async () => {
    const { type, id } = deleteConfirm;
    
    if (type === 'user') {
      await handleDeleteUser(id);
    } else if (type === 'blog') {
      await handleDeleteBlog(id);
    } else if (type === 'file') {
      await handleDeleteFile(id);
    } else if (type === 'enquiry') {
      await handleDelete(id);
    }
  };

  const openEditUser = (user) => {
    setEditUser(user);
    setEditUserForm({
      email: user.email,
      full_name: user.full_name,
      password: '',
      role: user.role || 'admin'
    });
    setIsEditUserOpen(true);
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('admin_token');
      const updateData = {
        email: editUserForm.email,
        full_name: editUserForm.full_name,
        role: editUserForm.role
      };
      if (editUserForm.password) {
        updateData.password = editUserForm.password;
      }
      
      await axios.put(
        `${BACKEND_URL}/api/admin/users/${editUser.user_id}`,
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('User updated successfully');
      setIsEditUserOpen(false);
      setEditUser(null);
      fetchData();
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleUpdateStatus = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('admin_token');
      await axios.patch(
        `${BACKEND_URL}/api/admin/enquiries/${statusUpdate.enquiry_id}/status`,
        { status: statusUpdate.status, remarks: statusUpdate.remarks },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Status updated successfully');
      setIsStatusUpdateOpen(false);
      setStatusUpdate({ enquiry_id: '', status: '', remarks: '' });
      fetchData();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const openStatusUpdate = (enquiry) => {
    setStatusUpdate({
      enquiry_id: enquiry.enquiry_id,
      status: enquiry.status || 'new',
      remarks: enquiry.remarks || ''
    });
    setIsStatusUpdateOpen(true);
  };

  const getStatusColor = (status) => {
    const colors = {
      'new': 'bg-blue-100 text-blue-800',
      'contacted': 'bg-yellow-100 text-yellow-800',
      'in_progress': 'bg-purple-100 text-purple-800',
      'qualified': 'bg-green-100 text-green-800',
      'converted': 'bg-emerald-100 text-emerald-800',
      'closed': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const handleBlogFormChange = (e) => {
    setBlogForm({
      ...blogForm,
      [e.target.name]: e.target.value
    });
  };

  const handleCreateBlog = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('admin_token');
      const blogData = {
        title: blogForm.title,
        subtitle: blogForm.subtitle,
        content: blogForm.content,
        image_url: blogForm.image_url,
        hashtags: blogForm.hashtags ? blogForm.hashtags.split(',').map(t => t.trim()) : [],
        published: blogForm.published
      };
      
      await axios.post(`${BACKEND_URL}/api/admin/blog/posts`, blogData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Blog post created successfully!');
      setIsBlogFormOpen(false);
      setBlogForm({ blog_id: '', title: '', subtitle: '', content: '', image_url: '', hashtags: '', published: true });
      fetchData();
    } catch (error) {
      console.error('Error creating blog:', error);
      toast.error('Failed to create blog post');
    }
  };

  const handleEditBlog = (blog) => {
    setBlogForm({
      blog_id: blog.blog_id,
      title: blog.title,
      subtitle: blog.subtitle || '',
      content: blog.content,
      image_url: blog.image_url || '',
      hashtags: blog.hashtags ? blog.hashtags.join(', ') : '',
      published: blog.published
    });
    setIsBlogFormOpen(true);
  };

  // Preview blog post
  const openBlogPreview = (blog) => {
    setPreviewBlog(blog);
    setIsPreviewOpen(true);
  };

  // Preview from form (while creating/editing)
  const previewFromForm = () => {
    const previewData = {
      blog_id: blogForm.blog_id || 'preview',
      title: blogForm.title || 'Untitled',
      subtitle: blogForm.subtitle,
      content: blogForm.content || '<p>No content yet...</p>',
      image_url: blogForm.image_url,
      hashtags: blogForm.hashtags ? blogForm.hashtags.split(',').map(t => t.trim()).filter(t => t) : [],
      author: 'GoldenPort Team',
      created_at: new Date().toISOString(),
      published: blogForm.published
    };
    setPreviewBlog(previewData);
    setIsPreviewOpen(true);
  };

  const handleUpdateBlog = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('admin_token');
      const blogData = {
        title: blogForm.title,
        subtitle: blogForm.subtitle,
        content: blogForm.content,
        image_url: blogForm.image_url,
        hashtags: blogForm.hashtags ? blogForm.hashtags.split(',').map(t => t.trim()) : [],
        published: blogForm.published
      };
      
      await axios.put(`${BACKEND_URL}/api/admin/blog/posts/${blogForm.blog_id}`, blogData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Blog post updated successfully!');
      setIsBlogFormOpen(false);
      setBlogForm({ blog_id: '', title: '', subtitle: '', content: '', image_url: '', hashtags: '', published: true });
      fetchData();
    } catch (error) {
      console.error('Error updating blog:', error);
      toast.error('Failed to update blog post');
    }
  };

  const handleDeleteBlog = async (blogId) => {
    setDeleting(true);
    try {
      const token = localStorage.getItem('admin_token');
      console.log('Deleting blog:', blogId);
      await axios.delete(`${BACKEND_URL}/api/admin/blog/posts/${blogId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Blog post deleted successfully');
      setDeleteConfirm({ open: false, type: '', id: '', name: '' });
      fetchData();
    } catch (error) {
      console.error('Error deleting blog:', error);
      toast.error('Failed to delete blog post');
    } finally {
      setDeleting(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingFile(true);
    try {
      const token = localStorage.getItem('admin_token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${BACKEND_URL}/api/admin/files/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success('File uploaded successfully!');
      fetchData();
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploadingFile(false);
      // Reset input
      e.target.value = '';
    }
  };

  const handleDeleteFile = async (fileId) => {
    setDeleting(true);
    try {
      const token = localStorage.getItem('admin_token');
      console.log('Deleting file:', fileId);
      await axios.delete(`${BACKEND_URL}/api/admin/files/${fileId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('File deleted successfully');
      setDeleteConfirm({ open: false, type: '', id: '', name: '' });
      fetchData();
    } catch (error) {
      console.error('Error deleting file:', error);
      toast.error('Failed to delete file');
    } finally {
      setDeleting(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('URL copied to clipboard!');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-2xl text-[#0B2C4D]">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-[#0B2C4D] text-white shadow-lg">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_bec05211-66fe-40f4-be9b-9c2beecb0c73/artifacts/bvs625is_Logogo.png" 
                alt="GoldenPort" 
                className="h-12 w-auto"
              />
              <h1 className="text-2xl font-bold">Admin Dashboard</h1>
            </div>
            <Button
              onClick={handleLogout}
              className="bg-[#C9A24D] hover:bg-[#b8923e] text-[#0B2C4D]"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Dashboard */}
      <div className="container mx-auto px-6 py-8">
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-[#C9A24D]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-semibold">Total Enquiries</p>
                <p className="text-3xl font-bold text-[#0B2C4D] mt-2" data-testid="total-enquiries">{stats.total_enquiries}</p>
              </div>
              <Users className="w-12 h-12 text-[#C9A24D]" />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-semibold">New Today</p>
                <p className="text-3xl font-bold text-[#0B2C4D] mt-2" data-testid="new-today">{stats.new_today}</p>
              </div>
              <Mail className="w-12 h-12 text-green-500" />
            </div>
          </div>

          <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-blue-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm font-semibold">Export Requests</p>
                <p className="text-3xl font-bold text-[#0B2C4D] mt-2" data-testid="export-requests">{stats.total_exports}</p>
              </div>
              <Package className="w-12 h-12 text-blue-500" />
            </div>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="bg-white rounded-lg shadow-md p-2 mb-6 flex flex-wrap gap-2">
          <button
            onClick={() => setActiveTab('enquiries')}
            data-testid="enquiries-tab"
            className={`flex-1 min-w-[120px] py-3 px-4 rounded-md font-semibold transition-colors ${
              activeTab === 'enquiries' 
                ? 'bg-[#0B2C4D] text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Mail className="w-5 h-5 inline mr-2" />
            Enquiries
          </button>
          <button
            onClick={() => setActiveTab('products')}
            data-testid="products-tab"
            className={`flex-1 min-w-[120px] py-3 px-4 rounded-md font-semibold transition-colors ${
              activeTab === 'products' 
                ? 'bg-[#0B2C4D] text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <ShoppingBag className="w-5 h-5 inline mr-2" />
            Products
          </button>
          <button
            onClick={() => setActiveTab('exports')}
            data-testid="exports-tab"
            className={`flex-1 min-w-[120px] py-3 px-4 rounded-md font-semibold transition-colors ${
              activeTab === 'exports' 
                ? 'bg-[#0B2C4D] text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Package className="w-5 h-5 inline mr-2" />
            Exports
          </button>
          <button
            onClick={() => setActiveTab('users')}
            data-testid="users-tab"
            className={`flex-1 min-w-[120px] py-3 px-4 rounded-md font-semibold transition-colors ${
              activeTab === 'users' 
                ? 'bg-[#0B2C4D] text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Users className="w-5 h-5 inline mr-2" />
            Team
          </button>
          <button
            onClick={() => setActiveTab('blogs')}
            data-testid="blogs-tab"
            className={`flex-1 min-w-[120px] py-3 px-4 rounded-md font-semibold transition-colors ${
              activeTab === 'blogs' 
                ? 'bg-[#0B2C4D] text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <FileText className="w-5 h-5 inline mr-2" />
            Blogs
          </button>
          <button
            onClick={() => setActiveTab('files')}
            data-testid="files-tab"
            className={`flex-1 min-w-[120px] py-3 px-4 rounded-md font-semibold transition-colors ${
              activeTab === 'files' 
                ? 'bg-[#0B2C4D] text-white' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            <Image className="w-5 h-5 inline mr-2" />
            Files
          </button>
        </div>

        {/* ==================== Products Tab ==================== */}
        {activeTab === 'products' && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8" data-testid="products-section">
            <h2 className="text-2xl font-bold text-[#0B2C4D] mb-6">Product Management</h2>
            <ProductManagement />
          </div>
        )}

        {/* ==================== Enquiries Tab ==================== */}
        {activeTab === 'enquiries' && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-8" data-testid="enquiries-section">
            <h2 className="text-2xl font-bold text-[#0B2C4D] mb-6">Contact Enquiries</h2>
            
            {enquiries.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No enquiries yet</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b-2 border-[#C9A24D]">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-[#0B2C4D]">Name</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-[#0B2C4D]">Email</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-[#0B2C4D]">Mobile</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-[#0B2C4D]">Company</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-[#0B2C4D]">Date</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-[#0B2C4D]">Status</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-[#0B2C4D]">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {enquiries.map((enquiry) => (
                      <tr key={enquiry.enquiry_id} className="border-b hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm">{enquiry.full_name}</td>
                        <td className="px-4 py-3 text-sm">{enquiry.email}</td>
                        <td className="px-4 py-3 text-sm">{enquiry.mobile}</td>
                        <td className="px-4 py-3 text-sm">{enquiry.company_name || '-'}</td>
                        <td className="px-4 py-3 text-sm">
                          {new Date(enquiry.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${getStatusColor(enquiry.status || 'new')}`}>
                            {(enquiry.status || 'new').replace('_', ' ').toUpperCase()}
                          </span>
                          {enquiry.remarks && (
                            <p className="text-xs text-gray-500 mt-1">{enquiry.remarks.substring(0, 30)}...</p>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <div className="flex gap-2">
                            <button
                              onClick={() => alert(`Message: ${enquiry.message}`)}
                              className="text-blue-600 hover:text-blue-800 p-1"
                              title="View Message"
                              data-testid={`view-enquiry-${enquiry.enquiry_id}`}
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => openStatusUpdate(enquiry)}
                              className="text-purple-600 hover:text-purple-800 p-1"
                              title="Update Status"
                              data-testid={`edit-enquiry-${enquiry.enquiry_id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => openDeleteConfirm('enquiry', enquiry.enquiry_id, enquiry.full_name)}
                              className="text-red-600 hover:text-red-800 p-1"
                              title="Delete"
                              data-testid={`delete-enquiry-${enquiry.enquiry_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Status Update Dialog */}
        <Dialog open={isStatusUpdateOpen} onOpenChange={setIsStatusUpdateOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Update Enquiry Status</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdateStatus} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Status</label>
                <Select value={statusUpdate.status} onValueChange={(value) => setStatusUpdate({...statusUpdate, status: value})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="contacted">Contacted</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="qualified">Qualified</SelectItem>
                    <SelectItem value="converted">Converted</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Remarks</label>
                <Textarea
                  value={statusUpdate.remarks}
                  onChange={(e) => setStatusUpdate({...statusUpdate, remarks: e.target.value})}
                  placeholder="Add notes or remarks..."
                  rows={4}
                />
              </div>
              <Button type="submit" className="w-full bg-[#0B2C4D] hover:bg-[#0a2440]" data-testid="update-status-btn">
                Update Status
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* ==================== Export Tracking Tab ==================== */}
        {activeTab === 'exports' && (
          <div className="bg-white rounded-lg shadow-md p-6" data-testid="exports-section">
            <h2 className="text-2xl font-bold text-[#0B2C4D] mb-6">Export Tracking</h2>
            
            {exports.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No export records yet</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b-2 border-[#C9A24D]">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-[#0B2C4D]">Export ID</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-[#0B2C4D]">Product</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-[#0B2C4D]">Destination</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-[#0B2C4D]">Status</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-[#0B2C4D]">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {exports.map((exp) => (
                      <tr key={exp.export_id} className="border-b hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-mono">{exp.export_id}</td>
                        <td className="px-4 py-3 text-sm">{exp.product_name}</td>
                        <td className="px-4 py-3 text-sm">{exp.destination}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${
                            exp.status === 'delivered' ? 'bg-green-100 text-green-800' :
                            exp.status === 'in_transit' ? 'bg-blue-100 text-blue-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {exp.status.replace('_', ' ').toUpperCase()}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {new Date(exp.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ==================== Team Members Tab ==================== */}
        {activeTab === 'users' && (
          <div className="bg-white rounded-lg shadow-md p-6" data-testid="users-section">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-[#0B2C4D]">Team Members</h2>
              <Dialog open={isAddUserOpen} onOpenChange={setIsAddUserOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-[#C9A24D] hover:bg-[#b8923e] text-[#0B2C4D]" data-testid="add-user-btn">
                    <UserPlus className="w-4 h-4 mr-2" />
                    Add User
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add New User</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddUser} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Email</label>
                      <Input
                        type="email"
                        value={newUser.email}
                        onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                        required
                        data-testid="new-user-email"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Full Name</label>
                      <Input
                        type="text"
                        value={newUser.full_name}
                        onChange={(e) => setNewUser({...newUser, full_name: e.target.value})}
                        required
                        data-testid="new-user-name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Password</label>
                      <Input
                        type="password"
                        value={newUser.password}
                        onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                        required
                        data-testid="new-user-password"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Role</label>
                      <Select value={newUser.role} onValueChange={(value) => setNewUser({...newUser, role: value})}>
                        <SelectTrigger data-testid="new-user-role">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="manager">Manager</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button type="submit" className="w-full bg-[#0B2C4D] hover:bg-[#0a2440]" data-testid="submit-add-user">
                      Add User
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            
            {adminUsers.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No users yet</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b-2 border-[#C9A24D]">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-[#0B2C4D]">Name</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-[#0B2C4D]">Email</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-[#0B2C4D]">Role</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-[#0B2C4D]">Created</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-[#0B2C4D]">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adminUsers.map((user) => (
                      <tr key={user.user_id} className="border-b hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm">{user.full_name}</td>
                        <td className="px-4 py-3 text-sm">{user.email}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${
                            user.role === 'super_admin' ? 'bg-red-100 text-red-800' :
                            user.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                          }`}>
                            {user.role ? user.role.toUpperCase().replace('_', ' ') : 'USER'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {new Date(user.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <div className="flex gap-2">
                            <button
                              onClick={() => openEditUser(user)}
                              className="text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded transition-colors"
                              title="Edit User"
                              data-testid={`edit-user-${user.user_id}`}
                            >
                              <Edit className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => openDeleteConfirm('user', user.user_id, user.email)}
                              className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded transition-colors"
                              title="Delete User"
                              data-testid={`delete-user-${user.user_id}`}
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Edit User Dialog */}
        <Dialog open={isEditUserOpen} onOpenChange={setIsEditUserOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit User</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleEditUser} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Email</label>
                <Input
                  type="email"
                  value={editUserForm.email}
                  onChange={(e) => setEditUserForm({...editUserForm, email: e.target.value})}
                  required
                  data-testid="edit-user-email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Full Name</label>
                <Input
                  type="text"
                  value={editUserForm.full_name}
                  onChange={(e) => setEditUserForm({...editUserForm, full_name: e.target.value})}
                  required
                  data-testid="edit-user-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">New Password (leave blank to keep current)</label>
                <Input
                  type="password"
                  value={editUserForm.password}
                  onChange={(e) => setEditUserForm({...editUserForm, password: e.target.value})}
                  placeholder="Enter new password"
                  data-testid="edit-user-password"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Role</label>
                <Select value={editUserForm.role} onValueChange={(value) => setEditUserForm({...editUserForm, role: value})}>
                  <SelectTrigger data-testid="edit-user-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" className="w-full bg-[#0B2C4D] hover:bg-[#0a2440]" data-testid="submit-edit-user">
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* ==================== Blog Posts Tab ==================== */}
        {activeTab === 'blogs' && (
          <div className="bg-white rounded-lg shadow-md p-6" data-testid="blogs-section">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-[#0B2C4D]">Blog Posts</h2>
              <Button 
                onClick={() => {
                  setBlogForm({ blog_id: '', title: '', subtitle: '', content: '', image_url: '', hashtags: '', published: true });
                  setIsBlogFormOpen(true);
                }}
                className="bg-[#C9A24D] hover:bg-[#b8923e] text-[#0B2C4D]"
                data-testid="add-blog-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Blog Post
              </Button>
            </div>
            
            {blogPosts.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No blog posts yet. Create your first blog post!</p>
            ) : (
              <div className="grid gap-4">
                {blogPosts.map((blog) => (
                  <div key={blog.blog_id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-semibold text-[#0B2C4D]">{blog.title}</h3>
                          <span className={`px-2 py-0.5 rounded text-xs ${blog.published ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                            {blog.published ? 'Published' : 'Draft'}
                          </span>
                        </div>
                        {blog.subtitle && <p className="text-gray-600 text-sm mt-1">{blog.subtitle}</p>}
                        <p className="text-gray-500 text-xs mt-2">
                          Created: {new Date(blog.created_at).toLocaleDateString()}
                        </p>
                        {blog.hashtags && blog.hashtags.length > 0 && (
                          <div className="flex gap-1 mt-2">
                            {blog.hashtags.map((tag, i) => (
                              <span key={i} className="text-xs bg-gray-100 px-2 py-0.5 rounded">#{tag}</span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => openBlogPreview(blog)}
                          className="text-green-600 hover:text-green-800 p-2 hover:bg-green-50 rounded"
                          title="Preview"
                          data-testid={`preview-blog-${blog.blog_id}`}
                        >
                          <Eye className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleEditBlog(blog)}
                          className="text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded"
                          title="Edit"
                          data-testid={`edit-blog-${blog.blog_id}`}
                        >
                          <Edit className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => openDeleteConfirm('blog', blog.blog_id, blog.title)}
                          className="text-red-600 hover:text-red-800 p-2 hover:bg-red-50 rounded"
                          title="Delete"
                          data-testid={`delete-blog-${blog.blog_id}`}
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Blog Form Dialog */}
        <Dialog open={isBlogFormOpen} onOpenChange={setIsBlogFormOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{blogForm.blog_id ? 'Edit Blog Post' : 'Create New Blog Post'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={blogForm.blog_id ? handleUpdateBlog : handleCreateBlog} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Title *</label>
                <Input
                  name="title"
                  value={blogForm.title}
                  onChange={handleBlogFormChange}
                  required
                  placeholder="Blog post title"
                  data-testid="blog-title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Subtitle</label>
                <Input
                  name="subtitle"
                  value={blogForm.subtitle}
                  onChange={handleBlogFormChange}
                  placeholder="Optional subtitle"
                  data-testid="blog-subtitle"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Image URL</label>
                <Input
                  name="image_url"
                  value={blogForm.image_url}
                  onChange={handleBlogFormChange}
                  placeholder="https://example.com/image.jpg"
                  data-testid="blog-image-url"
                />
                <p className="text-xs text-gray-500 mt-1">Upload images in Files tab to get URLs</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Content *</label>
                <Textarea
                  name="content"
                  value={blogForm.content}
                  onChange={handleBlogFormChange}
                  required
                  rows={12}
                  placeholder="Write your blog content here. HTML tags supported: <b>bold</b>, <i>italic</i>, <br> for line break, <p>paragraph</p>, <h2>heading</h2>, <ul><li>list</li></ul>"
                  data-testid="blog-content"
                />
                <p className="text-xs text-gray-500 mt-1">HTML formatting supported for rich content</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Hashtags</label>
                <Input
                  name="hashtags"
                  value={blogForm.hashtags}
                  onChange={handleBlogFormChange}
                  placeholder="export, trade, business (comma separated)"
                  data-testid="blog-hashtags"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="published"
                  checked={blogForm.published}
                  onChange={(e) => setBlogForm({...blogForm, published: e.target.checked})}
                  className="w-4 h-4"
                  data-testid="blog-published"
                />
                <label className="text-sm font-medium">Publish immediately</label>
              </div>
              <div className="flex gap-2">
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={previewFromForm}
                  className="flex-1 border-green-600 text-green-600 hover:bg-green-50"
                  data-testid="preview-blog-btn"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Preview
                </Button>
                <Button type="submit" className="flex-1 bg-[#0B2C4D] hover:bg-[#0a2440]" data-testid="submit-blog">
                  {blogForm.blog_id ? 'Update Blog Post' : 'Create Blog Post'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Blog Preview Dialog */}
        <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10">
              <div className="flex items-center gap-3">
                <Eye className="w-5 h-5 text-green-600" />
                <span className="font-semibold text-[#0B2C4D]">Blog Preview</span>
                {previewBlog && !previewBlog.published && (
                  <span className="px-2 py-0.5 rounded text-xs bg-yellow-100 text-yellow-800">Draft</span>
                )}
              </div>
              <span className="text-xs text-gray-500">This is how your blog will appear on the website</span>
            </div>
            
            {previewBlog && (
              <div className="bg-gray-50">
                {/* Simulated Blog Page Header */}
                <div className="bg-[#0B2C4D] text-white py-8">
                  <div className="container mx-auto px-6 text-center">
                    <p className="text-sm text-gray-300 mb-2">GoldenPort International Blog</p>
                  </div>
                </div>
                
                {/* Blog Content Preview */}
                <div className="container mx-auto px-6 py-8 max-w-3xl">
                  <div className="bg-white rounded-lg shadow-lg overflow-hidden border-t-4 border-[#C9A24D]">
                    {previewBlog.image_url && (
                      <div 
                        className="h-64 bg-cover bg-center"
                        style={{ backgroundImage: `url('${previewBlog.image_url}')` }}
                      />
                    )}
                    <div className="p-8">
                      <h1 className="text-3xl font-bold text-[#0B2C4D] mb-3">
                        {previewBlog.title}
                      </h1>
                      {previewBlog.subtitle && (
                        <p className="text-xl text-gray-600 mb-4">{previewBlog.subtitle}</p>
                      )}
                      
                      <div className="flex items-center gap-4 text-sm text-gray-500 mb-6 pb-6 border-b">
                        <div className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          <span>{previewBlog.author || 'GoldenPort Team'}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          <span>{new Date(previewBlog.created_at).toLocaleDateString('en-IN', { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric' 
                          })}</span>
                        </div>
                      </div>

                      {previewBlog.hashtags && previewBlog.hashtags.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-6">
                          {previewBlog.hashtags.map((tag, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center gap-1 bg-gray-100 text-[#0B2C4D] px-3 py-1 rounded-full text-sm"
                            >
                              <Tag className="w-3 h-3" />
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}

                      <div 
                        className="prose prose-lg max-w-none text-gray-700"
                        dangerouslySetInnerHTML={{ __html: previewBlog.content }}
                      />
                      
                      {/* Contact CTA */}
                      <div className="mt-8 p-6 bg-gradient-to-r from-[#0B2C4D] to-[#1a3a5a] rounded-lg text-white text-center">
                        <p className="text-lg font-semibold mb-2">Interested in our export services?</p>
                        <p className="text-sm text-gray-200 mb-4">Contact us today to start your export journey</p>
                        <button className="bg-[#C9A24D] hover:bg-[#b8923e] text-[#0B2C4D] px-6 py-2 rounded font-semibold">
                          Contact Us
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* ==================== Files & Images Tab ==================== */}
        {activeTab === 'files' && (
          <div className="bg-white rounded-lg shadow-md p-6" data-testid="files-section">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-[#0B2C4D]">Files & Images</h2>
              <div className="flex gap-2">
                <input
                  type="file"
                  id="file-upload"
                  className="hidden"
                  onChange={handleFileUpload}
                  accept="image/*,.pdf,.doc,.docx"
                />
                <Button 
                  onClick={() => document.getElementById('file-upload').click()}
                  className="bg-[#C9A24D] hover:bg-[#b8923e] text-[#0B2C4D]"
                  disabled={uploadingFile}
                  data-testid="upload-file-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {uploadingFile ? 'Uploading...' : 'Upload File'}
                </Button>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-800">
                <strong>Tip:</strong> Upload images here and copy the URL to use in blog posts. 
                Supported formats: JPG, PNG, GIF, PDF, DOC
              </p>
            </div>
            
            {files.length === 0 ? (
              <div className="text-center py-12">
                <Image className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500">No files uploaded yet</p>
                <p className="text-gray-400 text-sm mt-2">Upload images to use in your blog posts</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {files.map((file) => {
                  const fullUrl = file.url?.startsWith('http') ? file.url : `${BACKEND_URL}${file.url}`;
                  return (
                    <div key={file.file_id} className="border rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                      {file.content_type?.startsWith('image/') ? (
                        <img 
                          src={fullUrl} 
                          alt={file.filename}
                          className="w-full h-40 object-cover"
                        />
                      ) : (
                        <div className="w-full h-40 bg-gray-100 flex items-center justify-center">
                          <FileText className="w-12 h-12 text-gray-400" />
                        </div>
                      )}
                      <div className="p-3">
                        <p className="text-sm font-medium truncate" title={file.filename}>{file.filename}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(file.created_at).toLocaleDateString()}
                        </p>
                        <div className="flex gap-2 mt-2">
                          <button
                            onClick={() => copyToClipboard(fullUrl)}
                            className="flex-1 text-sm bg-gray-100 hover:bg-gray-200 py-1 px-2 rounded flex items-center justify-center gap-1"
                            title="Copy URL"
                            data-testid={`copy-url-${file.file_id}`}
                          >
                            <Copy className="w-3 h-3" /> Copy URL
                          </button>
                          <button
                            onClick={() => openDeleteConfirm('file', file.file_id, file.filename)}
                            className="text-red-600 hover:text-red-800 p-1 hover:bg-red-50 rounded"
                            title="Delete"
                            data-testid={`delete-file-${file.file_id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteConfirm.open} onOpenChange={(open) => !deleting && setDeleteConfirm({...deleteConfirm, open})}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="w-5 h-5" />
                Confirm Delete
              </DialogTitle>
              <DialogDescription className="text-gray-600 pt-2">
                Are you sure you want to delete this {deleteConfirm.type}?
                <br />
                <strong className="text-gray-800">{deleteConfirm.name}</strong>
                <br />
                <span className="text-red-500 text-sm">This action cannot be undone.</span>
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex gap-2 sm:gap-0">
              <Button
                variant="outline"
                onClick={() => setDeleteConfirm({ open: false, type: '', id: '', name: '' })}
                disabled={deleting}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={executeDelete}
                disabled={deleting}
                className="bg-red-600 hover:bg-red-700"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminDashboard;
